import { ArrowLeft } from '@phosphor-icons/react';
import { createColumnHelper } from '@tanstack/react-table';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { useMediaQuery } from 'usehooks-ts';

import {
  ActivityReportNewbuildUnitMixContainedUnits,
  DateRangeType,
} from '@plot/plot-api';
import { Table2, Tooltip } from '@plot/ui';

import { useGetActivityReport } from '@/features/activity-reports/queries';
import { useArListedNewbuildsUnitsTable } from '@/features/activity-reports/queries/use-ar-listed-newbuilds-units-table';
import { useArSoldNewbuildsUnitsTable } from '@/features/activity-reports/queries/use-ar-sold-newbuilds-units-table';
import { useWorkspaceId } from '@/lib/contexts';
import { convertKeysToSnakeCase, geoJSONToEwkbHex } from '@/lib/utils';

import { TypePeriod } from '@/components/activity-reports/period-selector';
import PriceDisplay from '@/components/common/PriceDisplay';

const useColumns = () => {
  const isMd = useMediaQuery('(min-width: 768px)'); // Tailwind's md breakpoint
  const is2xl = useMediaQuery('(min-width: 1536px)'); // Tailwind's 2xl breakpoint
  const t = useTranslations('ActivityReports.ActivityReport.UnitCategory');

  const columnHelper =
    createColumnHelper<ActivityReportNewbuildUnitMixContainedUnits>();

  const columns = [
    columnHelper.accessor('name', {
      header: () => (
        <div className="flex items-center gap-1">{t('table.name')}</div>
      ),
      cell: ({ row }) => (
        <span className="text-left line-clamp-1">
          <Tooltip content={row.original.name || '-'}>
            <span className="break-words line-clamp-1">
              {row.original.name || '-'}
            </span>
          </Tooltip>
        </span>
      ),
      enableSorting: true,
      size: is2xl ? 160 : isMd ? 160 : 110,
    }),
    columnHelper.accessor('project.name', {
      header: () => t('table.project'),
      cell: ({ row }) => (
        <span className="text-left line-clamp-1">
          <Tooltip content={row.original.project.name || '-'}>
            <span className="break-words line-clamp-1">
              {row.original.project.name || '-'}
            </span>
          </Tooltip>
        </span>
      ),
      enableSorting: true,
      size: is2xl ? 180 : 180,
    }),
    columnHelper.accessor('forSaleAt', {
      header: () => t('table.forSaleAt'),
      cell: ({ row }) => <span>{row.original.forSaleAt || '-'}</span>,
      size: is2xl ? 120 : 120,
      enableSorting: true,
    }),
    columnHelper.accessor('floor', {
      header: () => t('table.floor'),
      cell: ({ row }) => <span>{row.original.floor || '-'}</span>,
      size: is2xl ? 80 : 80,
      enableSorting: true,
    }),
    columnHelper.accessor('rooms', {
      header: () => t('table.rooms'),
      cell: ({ row }) => <span>{row.original.rooms || '-'}</span>,
      size: is2xl ? 90 : 90,
      enableSorting: true,
    }),
    columnHelper.accessor('usableArea', {
      header: () => t('table.usableArea'),
      cell: ({ row }) => (
        <>
          <span>{row.original.usableArea || '-'}</span>{' '}
          <span className="pl-0.5 lowercase text-neutral-80">m²</span>
        </>
      ),
      size: is2xl ? 70 : 70,
      enableSorting: true,
    }),
    columnHelper.accessor('sellingPrice', {
      header: () => t('table.sellingPrice'),
      cell: ({ row }) => (
        <>
          <PriceDisplay value={row.original.sellingPrice} />
        </>
      ),
      size: is2xl ? 120 : 120,
      enableSorting: true,
    }),
    columnHelper.accessor('totalSellingPriceChange', {
      header: () => t('table.totalSellingPriceChange'),
      cell: ({ row }) => (
        <>
          <PriceDisplay value={row.original.totalSellingPriceChange} compact />
        </>
      ),

      size: is2xl ? 120 : 120,
      enableSorting: true,
    }),
    columnHelper.accessor('finalizedAt', {
      header: () => t('table.finalizedAt'),
      cell: ({ row }) => <span>{row.original.finalizedAt || '-'}</span>,
      size: is2xl ? 120 : 120,
      enableSorting: true,
    }),
    columnHelper.accessor('daysOnMarket', {
      header: () => t('table.daysOnMarket'),
      cell: ({ row }) => (
        <div className="flex gap-1 lowercase">
          <span>{row.original.daysOnMarket || '-'}</span>
          <span className="text-neutral-40">{t('table.days')}</span>
        </div>
      ),
      size: is2xl ? 150 : 150,
      enableSorting: true,
    }),
  ];

  return columns;
};

export const ActivityReportNewBuildsUnitTable = () => {
  const workspaceId = useWorkspaceId();
  const navigate = useNavigate();
  const t = useTranslations('ActivityReports.ActivityReport.UnitCategory');

  const search = location.search;

  const { activityReportId } = useParams<{ activityReportId: string }>();
  const searchParams = new URLSearchParams(search);
  const selectedPeriod =
    (searchParams.get('period') as TypePeriod) || DateRangeType.WEEKS;

  const lastPeriodIncludes = searchParams.get('last_period_includes') as string;
  const type = searchParams.get('type') as string;

  const columns = useColumns();

  const { data: activityReportData, isLoading: isLoadingActivityReportData } =
    useGetActivityReport({
      workspaceId,
      activityReportId,
    });

  const { data: listedUnitsTableData, isLoading: isLoadingListedUnitsTable } =
    useArListedNewbuildsUnitsTable({
      workspaceId,
      filters: convertKeysToSnakeCase(activityReportData?.filters || {}),
      geometry: geoJSONToEwkbHex(
        activityReportData?.area.geometry as GeoJSON.Geometry
      ),
      areaId: activityReportData?.area.areaId,
      period: [selectedPeriod],
      lastPeriodIncludes,
      enabled: type === 'listed',
    });

  const { data: soldUnitsTableData, isLoading: isLoadingSoldUnitsTable } =
    useArSoldNewbuildsUnitsTable({
      workspaceId,
      filters: convertKeysToSnakeCase(activityReportData?.filters || {}),
      geometry: geoJSONToEwkbHex(
        activityReportData?.area.geometry as GeoJSON.Geometry
      ),
      areaId: activityReportData?.area.areaId,
      period: [selectedPeriod],
      lastPeriodIncludes,
      enabled: type === 'sold',
    });

  const isLoading =
    isLoadingActivityReportData ||
    isLoadingListedUnitsTable ||
    isLoadingSoldUnitsTable;

  const data =
    (type === 'listed'
      ? listedUnitsTableData
      : type === 'sold' && soldUnitsTableData) || [];

  return (
    <div className="absolute z-40 h-full w-full overflow-y-auto bg-white px-6 py-10 dark:bg-superDark-bgWhite md:px-18">
      <div className="m-auto w-full max-w-[1300px]">
        <button
          className="cap-base mb-6 flex items-center gap-2"
          onClick={() => navigate(`../${search}`)}
        >
          <ArrowLeft />
          <span>{t('back')}</span>
        </button>
        <h2 className="hd-base mb-4">
          {type === 'listed' ? t('listedUnits.title') : t('soldUnits.title')} (
          {Array.isArray(data) ? data.length : 0})
        </h2>
        {isLoading ? (
          <Skeleton height={300} />
        ) : (
          <div className="mb-4 rounded border border-superLight-border dark:border-superDark-bgHeaderLight">
            <Table2
              columns={columns}
              data={data as ActivityReportNewbuildUnitMixContainedUnits[]}
              compact
              grayHeader={false}
              noDataText="No data"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityReportNewBuildsUnitTable;
